import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import moment from "moment";

const GET_VEHICLE_REPLAY_SNAPSHOTS = gql`
  query GetVehicleReplaySnapshots(
    $vehicleId: ID!
    $start: DateTimeOffset
    $end: DateTimeOffset
  ) {
    node(id: $vehicleId) {
      ... on Vehicle {
        id
        vrm
        cameraSnapshots(
          filter: { vehicleId: $vehicleId, startTime: $start, endTime: $end }
        ) {
          edges {
            cursor
            node {
              capturedAt
              signedUrl
            }
          }
        }
      }
    }
  }
`;

const imageCache = new Map();

const prefetchImage = (imageUrl) => {
  if (!imageUrl || imageCache.has(imageUrl)) return;
  const img = new Image();
  img.onload = () => imageCache.set(imageUrl, true);
  img.src = imageUrl;
};

const MAX_ALLOWED_DIFF_MS = 5 * 60 * 1000;

const ImageOverlay = ({ vehicleId, timestamp, show, mousePosition }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const overlayRef = useRef(null);
  const lastLoadedTimestamp = useRef(null);
  const debounceTimer = useRef(null);
  const hasPreloadedAll = useRef(false);

  const [timeRange, setTimeRange] = useState({ start: null, end: null });

  useEffect(() => {
    if (!timestamp) return;

    const timestampMoment = moment(timestamp);

    setTimeRange({
      start: timestampMoment.clone().startOf("day").toISOString(),
      end: timestampMoment.clone().endOf("day").toISOString(),
    });
  }, [timestamp]);

  useEffect(() => {
    if (!show || !timestamp) {
      setImageUrl(null);
      setLoading(false);
      setError(false);
      lastLoadedTimestamp.current = null;
      hasPreloadedAll.current = false;
      return;
    }
    setError(false);
  }, [show, timestamp]);

  const {
    data: snapshotsData,
    loading: snapshotsLoading,
    error: snapshotsError,
  } = useQuery(GET_VEHICLE_REPLAY_SNAPSHOTS, {
    variables: {
      vehicleId,
      start: timeRange.start,
      end: timeRange.end,
    },
    skip: !vehicleId || !timeRange.start || !timeRange.end,
    fetchPolicy: "cache-first",
  });

  const images = useMemo(() => {
    const edges = snapshotsData?.node?.cameraSnapshots?.edges ?? [];
    return edges
      .map((edge) => ({
        timestamp: edge?.node?.capturedAt,
        url: edge?.node?.signedUrl,
      }))
      .filter((x) => x.timestamp && x.url);
  }, [snapshotsData]);

  const prefetchNearbyImages = (currentIndex, range = 10) => {
    const minIndex = Math.max(0, currentIndex - range);
    const maxIndex = Math.min(images.length - 1, currentIndex + range);
    for (let i = minIndex; i <= maxIndex; i++) {
      if (i !== currentIndex) prefetchImage(images[i].url);
    }
  };

  useEffect(() => {
    if (!timestamp || !show) return;

    if (snapshotsLoading) {
      setLoading(true);
      return;
    }

    if (snapshotsError) {
      setImageUrl(null);
      setLoading(false);
      setError(true);
      return;
    }

    if (!images.length) {
      setImageUrl(null);
      setLoading(false);
      setError(false);
      return;
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      const targetTime = new Date(timestamp).getTime();

      if (
        lastLoadedTimestamp.current &&
        Math.abs(targetTime - lastLoadedTimestamp.current) < 10_000
      ) {
        return;
      }

      var closestImage = images.toSorted((a, b) => {
        var aDelta = Math.abs(timestamp - new Date(a.timestamp).getTime());
        var bDelta = Math.abs(timestamp - new Date(b.timestamp).getTime());
        return aDelta - bDelta;
      })[0];

      if (!closestImage) {
        setImageUrl(null);
        setLoading(false);
        setError(false);
        lastLoadedTimestamp.current = targetTime;
        return;
      }
      var closestIndex = images.findIndex((elem) => elem === closestImage);
      const minDiff = Math.abs(
        timestamp - new Date(closestImage.timestamp).getTime(),
      );
      const imageWithinAcceptableTimeBuffer = minDiff <= MAX_ALLOWED_DIFF_MS;

      if (!imageWithinAcceptableTimeBuffer) {
        setImageUrl(null);
        setLoading(false);
        setError(false);
        lastLoadedTimestamp.current = targetTime;
        return;
      }

      const selectedUrl = closestImage.url;

      prefetchNearbyImages(closestIndex);

      if (imageCache.has(selectedUrl)) {
        setImageUrl(selectedUrl);
        setLoading(false);
        setError(false);
        lastLoadedTimestamp.current = targetTime;
        return;
      }

      setLoading(true);
      setError(false);

      const img = new Image();
      img.onload = () => {
        imageCache.set(selectedUrl, true);
        setImageUrl(selectedUrl);
        setLoading(false);
        setError(false);
        lastLoadedTimestamp.current = targetTime;
      };
      img.onerror = () => {
        setImageUrl(null);
        setLoading(false);
        setError(true);
      };
      img.src = selectedUrl;
    }, 10);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [timestamp, show, images, snapshotsLoading, snapshotsError]);

  if (!show || !timestamp || !imageUrl) return null;

  return (
    <div
      ref={overlayRef}
      style={{
        position: "fixed",
        left: mousePosition?.x,
        top: mousePosition?.y,
        zIndex: 10000,
        pointerEvents: "none",
        transform: mousePosition ? "none" : "translate(-50%, -50%)",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          padding: "12px",
          minWidth: "280px",
          maxWidth: "400px",
        }}
      >
        {loading && (
          <div
            style={{
              width: 300,
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f0f0f0",
              borderRadius: "4px",
            }}
          >
            <div style={{ color: "#666", fontSize: "14px" }}>Loading...</div>
          </div>
        )}

        {!loading && !error && imageUrl && (
          <div
            style={{
              width: 360,
              height: 203,
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <img
              src={imageUrl}
              alt="Journey replay"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageOverlay;
